// 媒体输出归一化工具（与 useGeneration 解耦的纯函数集合）
// 职责：从任意模型返回的原始对象中提取图片/视频/或可展示文本，并转换为统一结构
// - 避免在 React hook 中夹杂大量解析逻辑
// - 便于为将来新模型（音频 / PDF / 多模态）扩展

import { ModelResultExtractor } from '@/features/workspace/modelFormTypes';

export interface NormalizedMediaOutput {
  images?: { url: string }[];
  videos?: { url: string }[];
  raw?: unknown;
}

export type NormalizedOutput = NormalizedMediaOutput | string | null;

export const isNormalizedMediaOutput = (value: unknown): value is NormalizedMediaOutput => {
  return typeof value === 'object' && value !== null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const VIDEO_EXT_REGEX = /\.(mp4|mov|webm|mkv)(\?|$)/i;
const IMAGE_EXT_REGEX = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i;
const MEDIA_URL_REGEX = /https?:\/\/[^\s)]+/gi;

const NON_MEDIA_HOSTS = new Set([
  'api.replicate.com',
  'replicate.com',
  'stream.replicate.com',
  // 任务列表历史中，用户上传的参考图等输入资源统一走该图床域名
  // 在结果渲染与媒体提取阶段，将其视为“非输出媒体”，避免误当成模型输出展示
  'imageproxy.zhongzhuan.chat',
]);

type PathToken = string | number;

const tokenizePath = (path: string): PathToken[] => {
  if (typeof path !== 'string') return [];
  const normalized = path
    .trim()
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  return normalized.map((segment) => (/^\d+$/.test(segment) ? Number(segment) : segment));
};

const extractStringCandidates = (value: unknown): string[] => {
  if (isString(value)) {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    const collected: string[] = [];
    for (const item of value) {
      collected.push(...extractStringCandidates(item));
    }
    return collected;
  }

  if (isRecord(value)) {
    const inlineData =
      (isRecord(value.inline_data) && value.inline_data) ||
      (isRecord(value.inlineData) && value.inlineData);
    if (inlineData) {
      const data = inlineData.data;
      const mime =
        typeof inlineData.mime_type === 'string'
          ? inlineData.mime_type
          : typeof inlineData.mimeType === 'string'
            ? inlineData.mimeType
            : '';
      if (isString(data)) {
        const normalized = data.trim().replace(/\s+/g, '');
        if (normalized) {
          const normalizedMime = mime && mime.trim() ? mime.trim() : 'image/png';
          return [`data:${normalizedMime};base64,${normalized}`];
        }
      }
    }

    const b64json =
      typeof value.b64_json === 'string'
        ? value.b64_json
        : typeof value.b64Json === 'string'
          ? value.b64Json
          : null;
    if (b64json) {
      const normalized = b64json.trim().replace(/\s+/g, '');
      if (normalized) {
        return [`data:image/png;base64,${normalized}`];
      }
    }

    const candidate =
      value.url ??
      value.video_url ??
      value.videoUrl ??
      value.image_url ??
      value.imageUrl ??
      value.src;

    if (isString(candidate)) {
      const trimmed = candidate.trim();
      return trimmed ? [trimmed] : [];
    }
  }

  return [];
};

const removePathImmutable = (
  value: unknown,
  tokens: PathToken[],
  depth = 0
): { nextValue: unknown; changed: boolean } => {
  if (depth >= tokens.length) {
    return { nextValue: value, changed: false };
  }

  if (Array.isArray(value)) {
    const indexToken = tokens[depth];
    if (typeof indexToken !== 'number') {
      return { nextValue: value, changed: false };
    }
    const arr = value as unknown[];
    if (indexToken < 0 || indexToken >= arr.length) {
      return { nextValue: value, changed: false };
    }

    if (depth === tokens.length - 1) {
      const clone = arr.slice();
      clone.splice(indexToken, 1);
      return { nextValue: clone, changed: true };
    }

    const { nextValue: childValue, changed } = removePathImmutable(arr[indexToken], tokens, depth + 1);
    if (!changed) {
      return { nextValue: value, changed: false };
    }

    const clone = arr.slice();
    clone[indexToken] = childValue;
    return { nextValue: clone, changed: true };
  }

  if (isRecord(value)) {
    const token = tokens[depth];
    const key = typeof token === 'string' ? token : typeof token === 'number' ? String(token) : undefined;
    if (!key || !(key in value)) {
      return { nextValue: value, changed: false };
    }

    if (depth === tokens.length - 1) {
      const clone: Record<string, unknown> = { ...value };
      delete clone[key];
      return { nextValue: clone, changed: true };
    }

    const currentChild = value[key];
    const { nextValue: childValue, changed } = removePathImmutable(currentChild, tokens, depth + 1);
    if (!changed) {
      return { nextValue: value, changed: false };
    }

    const clone: Record<string, unknown> = { ...value };
    clone[key] = childValue;
    return { nextValue: clone, changed: true };
  }

  return { nextValue: value, changed: false };
};

const applyExcludePaths = (source: unknown, excludePaths?: string[]): unknown => {
  if (!excludePaths || excludePaths.length === 0) {
    return source;
  }

  let sanitized = source;
  for (const path of excludePaths) {
    if (!path) continue;
    const tokens = tokenizePath(path);
    if (tokens.length === 0) continue;
    const { nextValue } = removePathImmutable(sanitized, tokens);
    sanitized = nextValue;
  }

  return sanitized;
};

/**
 * 根据点号与数组下标路径安全读取嵌套属性。
 * - 支持 data.detail.video_url、data.items[0].url 等混合路径
 * - 当路径无效或中间节点为 null/undefined 时返回 undefined，而不是抛出异常
 * @example
 * ```ts
 * const video = getValueByPath(payload, 'data.detail.video_url');
 * const firstImage = getValueByPath(payload, 'data.items[0].url');
 * ```
 * @param obj 原始对象或数组
 * @param path 使用 . 和 [index] 表达的访问路径
 * @returns 命中路径的值，找不到时返回 undefined
 */
export const getValueByPath = (obj: unknown, path: string): unknown | undefined => {
  if (obj == null || typeof path !== 'string' || path.trim().length === 0) {
    return undefined;
  }

  const tokens = tokenizePath(path);
  if (tokens.length === 0) {
    return undefined;
  }

  let current: unknown = obj;

  for (const token of tokens) {
    if (current == null) {
      return undefined;
    }

    if (typeof token === 'number') {
      if (Array.isArray(current)) {
        current = current[token];
      } else if (isRecord(current)) {
        current = current[String(token)];
      } else {
        return undefined;
      }
      continue;
    }

    if (isRecord(current)) {
      current = current[token];
    } else if (Array.isArray(current)) {
      const arrayAsRecord = current as unknown as Record<string, unknown>;
      current = arrayAsRecord[token];
    } else {
      return undefined;
    }
  }

  return current ?? undefined;
};

// 递归提取媒体 URL（含 Gemini inline_data base64 支持）
export const extractMediaUrls = (obj: unknown): { images: string[]; videos: string[] } => {
  const images: string[] = [];
  const videos: string[] = [];

  const isLikelyBase64 = (s: string) => {
    const sanitized = s.replace(/\s+/g, '');
    if (sanitized.length <= 100) return false;
    if (sanitized.length % 4 !== 0) return false;
    return /^[A-Za-z0-9+/=]+$/.test(sanitized);
  };

  const pushIfUrl = (candidate: unknown) => {
    if (!isString(candidate)) return;
    const normalized = candidate.trim();
    if (!normalized) return;

    if (normalized.startsWith('base64,')) {
      const sanitized = normalized.slice('base64,'.length).replace(/\s+/g, '');
      if (sanitized.length > 0) {
        images.push(`data:image/png;base64,${sanitized}`);
      }
      return;
    }

    if (isLikelyBase64(normalized)) {
      images.push(`data:image/png;base64,${normalized.replace(/\s+/g, '')}`);
      return;
    }

    if (/^data:video\//i.test(normalized) || VIDEO_EXT_REGEX.test(normalized)) {
      videos.push(normalized);
      return;
    }

    if (/^data:image\//i.test(normalized)) {
      images.push(normalized);
      return;
    }

    if (IMAGE_EXT_REGEX.test(normalized)) {
      const hasPathOrProtocol = /[/:\\]/.test(normalized);
      if (!hasPathOrProtocol) {
        return;
      }

      images.push(normalized);
      return;
    }

    if (/^https?:\/\//i.test(normalized)) {
      if (VIDEO_EXT_REGEX.test(normalized)) {
        videos.push(normalized);
        return;
      }

      if (IMAGE_EXT_REGEX.test(normalized)) {
        images.push(normalized);
        return;
      }

      try {
        const { hostname } = new URL(normalized);
        if (NON_MEDIA_HOSTS.has(hostname.toLowerCase())) {
          return;
        }
      } catch {
        // 忽略无效 URL
        return;
      }

      images.push(normalized);
      return;
    }

    // 支持从 Markdown 文本中提取图片 URL（如 ![image](https://...)）
    // 用于处理 OpenAI Chat Completions 风格的图像生成响应
    const markdownImageMatches = normalized.matchAll(/!\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/gi);
    for (const match of markdownImageMatches) {
      const extractedUrl = match[1];
      if (!extractedUrl) continue;
      try {
        const { hostname } = new URL(extractedUrl);
        if (NON_MEDIA_HOSTS.has(hostname.toLowerCase())) continue;
      } catch {
        continue;
      }
      if (VIDEO_EXT_REGEX.test(extractedUrl)) {
        videos.push(extractedUrl);
      } else {
        images.push(extractedUrl);
      }
    }
  };

  const pushInlineBase64 = (inlineObj: unknown) => {
    if (!isRecord(inlineObj)) return;
    const data = inlineObj.data;
    const mt =
      typeof inlineObj.mime_type === 'string'
        ? inlineObj.mime_type
        : typeof inlineObj.mimeType === 'string'
          ? inlineObj.mimeType
          : '';

    if (!isString(data)) return;
    const sanitized = data.replace(/\s+/g, '');
    if (!sanitized) return;

    const mime = mt && mt.trim() ? mt.trim() : 'image/png';

    if (mime.startsWith('video/')) {
      videos.push(`data:${mime};base64,${sanitized}`);
    } else {
      const normalizedMime = mime.startsWith('image/') ? mime : 'image/png';
      images.push(`data:${normalizedMime};base64,${sanitized}`);
    }
  };

  const visited = new WeakSet<object>();
  const walk = (value: unknown) => {
    if (!isRecord(value)) return;
    if (visited.has(value)) return;
    visited.add(value);

    const directUrl =
      value.video_url ??
      value.result_url ??
      value.output_url ??
      value.file_url ??
      value.url ??
      value.image_url ??
      value.imageUrl ??
      value.src;
    pushIfUrl(directUrl);

    const candidates = value.candidates;
    if (Array.isArray(candidates)) {
      candidates.forEach(walk);
    }

    // 支持 Gemini 的 candidates[].content 结构
    const content = value.content;
    if (isRecord(content)) {
      walk(content);
    }

    const parts = value.parts;
    if (Array.isArray(parts)) {
      for (const part of parts) {
        if (isRecord(part)) {
          // 跳过只包含 thoughtSignature 的 part,避免无意义的 DFS
          const shouldSkipPart =
            typeof part.thoughtSignature === 'string' &&
            Object.keys(part).every((key) => key === 'thoughtSignature' || key === 'role');
          if (shouldSkipPart) {
            continue;
          }

          if (part.inline_data) pushInlineBase64(part.inline_data);
          if (part.inlineData) pushInlineBase64(part.inlineData);
          if (part.content) {
            if (isRecord(part.content)) {
              pushIfUrl(part.content.url ?? part.content);
            } else {
              pushIfUrl(part.content);
            }
          }
          walk(part);
        }
      }
    }

    const imagesArr = value.images;
    if (Array.isArray(imagesArr)) {
      for (const item of imagesArr) {
        if (isString(item)) {
          pushIfUrl(item);
        } else if (isRecord(item)) {
          pushIfUrl(item.url ?? item.image_url ?? item.imageUrl ?? item.src ?? item.content);
        }
      }
    }

    const files = value.files;
    if (Array.isArray(files)) {
      for (const file of files) {
        if (isString(file)) {
          pushIfUrl(file);
        } else if (isRecord(file)) {
          pushIfUrl(file.url ?? file.download_url ?? file.file_url ?? file.src);
        }
      }
    }

    const outputs = value.outputs;
    if (Array.isArray(outputs)) {
      for (const output of outputs) {
        if (isString(output)) {
          pushIfUrl(output);
        } else if (isRecord(output)) {
          if (output.content) {
            if (isRecord(output.content)) {
              pushIfUrl(output.content.url ?? output.content);
            } else {
              pushIfUrl(output.content);
            }
          } else {
            pushIfUrl(output.url ?? output.image_url ?? output.src);
          }
        }
        walk(output);
      }
    }

    if (value.inline_data) pushInlineBase64(value.inline_data);
    if (value.inlineData) pushInlineBase64(value.inlineData);

    if (value.raw) walk(value.raw);
    if (value.detail) walk(value.detail);

    // 跳过 Gemini inline_data 的 data 字段,避免在 pushInlineBase64 后再次处理
    const hasInlineMime = typeof value.mime_type === 'string' || typeof value.mimeType === 'string';
    if (value.data && !hasInlineMime) {
      const dataValue = value.data;
      if (isString(dataValue)) pushIfUrl(dataValue);
      else walk(dataValue);
    }

    for (const key of Object.keys(value)) {
      const lowerKey = key.toLowerCase();
      // 历史任务等响应中，input / inputs 通常承载的是“请求输入”（参考图、首帧等），
      // 而非模型生成结果；这里显式跳过这些分支，避免在任务列表中把输入资源当成输出渲染。
      // Gemini 的 contents 和 body 字段同样包含请求输入，也需要跳过。
      if (
        lowerKey === 'input' ||
        lowerKey === 'inputs' ||
        lowerKey === 'contents' ||
        lowerKey === 'body' ||
        (lowerKey === 'data' && hasInlineMime) ||
        key === 'thoughtSignature' ||
        lowerKey === 'thoughtsignature'
      ) {
        continue;
      }

      const nested = value[key];
      if (isRecord(nested) || Array.isArray(nested)) {
        walk(nested);
      } else {
        pushIfUrl(nested);
      }
    }
  };

  walk(obj);
  return { images: Array.from(new Set(images)), videos: Array.from(new Set(videos)) };
};

/**
 * 基于模型配置提取媒体资源，支持按路径优先级精准读取并排除输入字段。
 * - 当提供 videoPaths 时，依序读取并返回首个有效 URL
 * - imagePaths（如存在）用于采集全部图片 URL；否则回落到通用提取逻辑
 * - 在提取前会依据 excludePaths 构造浅拷贝，避免污染原始响应
 * @param result 模型原始响应对象
 * @param config 各模型自定义的提取配置
 * @returns { images: string[]; videos: string[] } 归一化的媒体 URL 列表
 */
export const extractWithConfig = (
  result: unknown,
  config?: ModelResultExtractor
): { images: string[]; videos: string[] } => {
  if (!config) {
    return extractMediaUrls(result);
  }

  const sanitizedResult = applyExcludePaths(result, config.excludePaths);
  let fallbackMedia: { images: string[]; videos: string[] } | undefined;
  const ensureFallbackMedia = () => {
    if (!fallbackMedia) {
      fallbackMedia = extractMediaUrls(sanitizedResult);
    }
    return fallbackMedia;
  };

  const videos: string[] = [];
  if (Array.isArray(config.videoPaths) && config.videoPaths.length > 0) {
    for (const path of config.videoPaths) {
      const values = extractStringCandidates(getValueByPath(sanitizedResult, path));
      if (values.length > 0) {
        videos.push(values[0]);
        break;
      }
    }
  } else {
    videos.push(...ensureFallbackMedia().videos);
  }

  const images: string[] = [];
  if (Array.isArray(config.imagePaths) && config.imagePaths.length > 0) {
    for (const path of config.imagePaths) {
      const values = extractStringCandidates(getValueByPath(sanitizedResult, path));
      if (values.length > 0) {
        images.push(...values);
      }
    }
  } else {
    images.push(...ensureFallbackMedia().images);
  }

  return {
    images: Array.from(new Set(images)),
    videos: Array.from(new Set(videos)),
  };
};

export const extractGeminiTextContent = (obj: unknown): string | null => {
  const texts: string[] = [];
  const thoughts: string[] = [];
  const visited = new WeakSet<object>();

  const walk = (value: unknown): void => {
    if (value == null) return;
    if (Array.isArray(value)) {
      for (const item of value) walk(item);
      return;
    }
    if (!isRecord(value)) return;
    if (visited.has(value)) return;
    visited.add(value);

    if (Array.isArray(value.candidates)) {
      for (const candidate of value.candidates) walk(candidate);
    }
    if (isRecord(value.content)) walk(value.content);

    if (Array.isArray(value.parts)) {
      for (const part of value.parts) {
        if (!isRecord(part)) continue;
        const thoughtFlag = part.thought;
        const isThought = typeof thoughtFlag === 'boolean' ? thoughtFlag : thoughtFlag === 'true';
        if (isString(part.text)) {
          if (isThought) {
            thoughts.push(part.text);
          } else {
            texts.push(part.text);
          }
        }
        walk(part);
      }
    }

    if (value.raw !== undefined) walk(value.raw);
    if (value.detail !== undefined) walk(value.detail);
    if (value.data !== undefined) walk(value.data);
  };

  walk(obj);

  // 优先返回非思考内容
  const merged = texts.join('\n');
  if (merged.trim().length > 0) {
    return merged;
  }

  // 如果没有非思考内容，返回思考内容作为 fallback
  // 这种情况可能发生在 thinking 模型只输出了思考过程时
  const mergedThoughts = thoughts.join('\n');
  if (mergedThoughts.trim().length > 0) {
    console.warn('[extractGeminiTextContent] 响应只包含思考内容，将思考内容作为输出返回');
    return mergedThoughts;
  }

  return null;
};

const isChatCompletionStream = (text: string): boolean => {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith('data:')) {
    return false;
  }
  return /"object"\s*:\s*"chat\.completion/i.test(text);
};

const normalizeStreamText = (rawText: string): NormalizedMediaOutput => {
  const normalizedText = rawText.replace(/\r\n/g, '\n');
  const textForRaw = normalizedText.trim() ? normalizedText.trim() : normalizedText;

  const urlMatches = normalizedText.match(MEDIA_URL_REGEX) ?? [];
  const uniqueUrls = Array.from(new Set(urlMatches));

  const images = uniqueUrls.filter((url) => IMAGE_EXT_REGEX.test(url) || url.startsWith('data:image/'));
  const videos = uniqueUrls.filter((url) => VIDEO_EXT_REGEX.test(url) || url.startsWith('data:video/'));

  const result: NormalizedMediaOutput = { raw: textForRaw };
  if (images.length > 0) {
    result.images = images.map((url) => ({ url }));
  }
  if (videos.length > 0) {
    result.videos = videos.map((url) => ({ url }));
  }

  return result;
};

export const normalizeFinalData = (finalData: unknown): NormalizedOutput => {
  if (finalData == null) return null;
  if (typeof finalData === 'string') {
    if (isChatCompletionStream(finalData)) {
      return normalizeStreamText(finalData);
    }
    return finalData;
  }

  if (isRecord(finalData) && typeof finalData.__sora_stream === 'string') {
    const streamText = finalData.__sora_stream as string;
    const rest = { ...(finalData as Record<string, unknown>) };
    delete rest.__sora_stream;
    const payloadCandidate = rest.__sora_payload;
    delete rest.__sora_payload;

    const payloadSource = payloadCandidate !== undefined ? payloadCandidate : Object.keys(rest).length > 0 ? rest : undefined;
    const payloadNormalized = payloadSource !== undefined ? normalizeFinalData(payloadSource) : null;

    const streamNormalized = normalizeStreamText(streamText);
    const mergedImages = new Map<string, { url: string }>();
    const mergedVideos = new Map<string, { url: string }>();

    if (streamNormalized.images) {
      for (const item of streamNormalized.images) {
        if (item && typeof item.url === 'string') {
          mergedImages.set(item.url, { url: item.url });
        }
      }
    }

    if (streamNormalized.videos) {
      for (const item of streamNormalized.videos) {
        if (item && typeof item.url === 'string') {
          mergedVideos.set(item.url, { url: item.url });
        }
      }
    }

    if (isNormalizedMediaOutput(payloadNormalized)) {
      if (payloadNormalized.images) {
        for (const item of payloadNormalized.images) {
          if (item && typeof item.url === 'string') {
            mergedImages.set(item.url, { url: item.url });
          }
        }
      }
      if (payloadNormalized.videos) {
        for (const item of payloadNormalized.videos) {
          if (item && typeof item.url === 'string') {
            mergedVideos.set(item.url, { url: item.url });
          }
        }
      }
    }

    const result: NormalizedMediaOutput = {
      raw: streamNormalized.raw ?? streamText,
    };

    if (mergedImages.size > 0) {
      result.images = Array.from(mergedImages.values());
    }

    if (mergedVideos.size > 0) {
      result.videos = Array.from(mergedVideos.values());
    }

    return result;
  }

  const geminiText = extractGeminiTextContent(finalData);

  if (isRecord(finalData)) {
    const detail = finalData.detail;
    const draftInfo = isRecord(detail) ? detail.draft_info ?? detail.draftInfo : undefined;
    const draftKind = isRecord(draftInfo) ? draftInfo.kind : undefined;
    const draftKindStr = typeof draftKind === 'string' ? draftKind.toLowerCase() : undefined;
    const isSoraAsyncDraft = Boolean(draftKindStr && draftKindStr.includes('sora'));

    if (isSoraAsyncDraft) {
      const detailVideo = isRecord(detail)
        ? typeof detail.video_url === 'string'
          ? detail.video_url
          : typeof detail.videoUrl === 'string'
            ? detail.videoUrl
            : undefined
        : undefined;

      const videoCandidate =
        typeof finalData.video_url === 'string'
          ? finalData.video_url
          : typeof finalData.videoUrl === 'string'
            ? finalData.videoUrl
            : detailVideo;

      if (isString(videoCandidate)) {
        const normalizedUrl = videoCandidate.trim();
        if (normalizedUrl) {
          return {
            videos: [{ url: normalizedUrl }],
            raw: finalData,
          };
        }
      }
    }
  }

  const media = extractMediaUrls(finalData);
  const hasImages = media.images.length > 0;
  const hasVideos = media.videos.length > 0;

  if (hasImages || hasVideos) {
    const result: NormalizedMediaOutput = { raw: geminiText ?? finalData };
    if (hasImages) {
      result.images = media.images.map((url) => ({ url }));
    }
    if (hasVideos) {
      result.videos = media.videos.map((url) => ({ url }));
    }
    return result;
  }

  if (geminiText) {
    return geminiText;
  }

  if (isRecord(finalData)) {
    const directUrl = finalData.url ?? finalData.image_url ?? finalData.imageUrl ?? finalData.src;
    if (isString(directUrl)) return { images: [{ url: directUrl }], raw: finalData };

    const base64 =
      finalData.image_base64 ??
      finalData.base64 ??
      finalData.b64 ??
      finalData.data;
    if (isString(base64) && /^[A-Za-z0-9+/=\s]+$/.test(base64) && base64.replace(/\s+/g, '').length > 100) {
      return { images: [{ url: `data:image/png;base64,${base64.replace(/\s+/g, '')}` }], raw: finalData };
    }
  }

  try {
    return JSON.stringify(finalData);
  } catch {
    return finalData as NormalizedOutput;
  }
};
